import { ActionCard, Section } from '@playbooks/core/src/interfaces/ActionCard'

import clone from '../clone'
import {
  removeExcludedNodes,
  removeNonessentialProperties,
  removeSectionNames,
  removeValues,
  traverseNodes,
} from '../actionCardOperations'

/**
 * Get adjusted Action Card sections that can be answered by the AI model.
 * @param sections The original Action Card sections
 * @param ignoreCollapsedSections Whether to include only the opened sections
 * @returns Adjusted sections
 * @story #4377; User Story 9721: New Action Card Widget: Update with AI
 */
export function getSectionsToBeAnswered(actionCard: ActionCard, ignoreCollapsedSections: boolean): Section[] {
  let prepared = clone(actionCard) as ActionCard
  // Include only the opened sections (for efficiency)
  if (ignoreCollapsedSections) {
    prepared.sections = prepared.sections.filter(section => section.isOpened)
  }
  // Remove all nodes that are marked as "excluded from AI"
  prepared = removeExcludedNodes(prepared)
  // Remove section names to avoid them being confused with questions
  prepared = removeSectionNames(prepared)
  // Remove all values to avoid them being used as a hint for the AI model
  prepared = removeValues(prepared)
  // Remove all non-essential sections
  prepared = removeNonessentialSections(prepared)
  // Remove all non-essential properties to optimize tokens use and generation speed
  prepared = removeNonessentialProperties(prepared)
  // Adjust predefined answer options arrays to be more consistent
  prepared = consolidateFieldNaming(prepared)
  console.log('Sections prepared for responses', prepared.sections)
  return prepared.sections
}

/**
 * Remove the sections which cannot be answered by the AI model.
 * @param actionCard The original Action Card
 * @returns Adjusted sections
 */
function removeNonessentialSections(actionCard: ActionCard): ActionCard {
  const essentialSectionTypes = new Set(['Form', 'Assessment', 'Notes', 'Content'])
  actionCard.sections = actionCard.sections.filter(section => essentialSectionTypes.has(section.type))
  return actionCard
}

/**
 * Adjust Action Card field naming to be more consistent,
 * so that it's easier to explain the structure to the AI model.
 * @param actionCard The original Action Card
 * @returns Adjusted Action Card
 */
function consolidateFieldNaming(actionCard: ActionCard): ActionCard {
  traverseNodes(actionCard, '*', (obj, key) => {
    // Rename predefined answer options arrays to be more consistent (#4377)
    if (key === 'responses' || key === 'data') {
      obj.options = obj[key]
      delete obj[key]
    }
    // Ensure form questions and assessments are named more consistently (#9721)
    if (key === 'assessment') {
      obj.questions = obj[key]
      delete obj[key]
    }
  })
  return actionCard
}
